import { forwardRef } from "react";
import {
  Alert as MuiAlert,
  Box,
  IconButton,
  Stack,
  type AlertProps as MuiAlertProps,
} from "@mui/material";

import { Icon, type IconName } from "../Icon";
import { tokens } from "../../tokens/generated/tokens";

import type { SxProps, Theme } from "@mui/material/styles";
import type { ReactNode } from "react";

/** Either color scheme's token set. The union makes every read valid in both. */
type SchemeTokens = (typeof tokens.theme)[keyof typeof tokens.theme];

/**
 * The dark values apply under the attribute `cssVariables` themes toggle, except
 * inside a nearer scope that pins light — `appRoutes` wraps the pre-auth pages
 * that way, and an unguarded descendant selector would still match through it,
 * putting a dark alert on a light page.
 */
const DARK_SCHEME =
  '[data-mui-color-scheme="dark"] &:not([data-mui-color-scheme="light"] *)';
const { weight } = tokens.base.typography.text;
const { body } = tokens.typography.typography.text;
const stackPaddingCondensed = `${tokens.pattern.size.stack.padding.condensed}px`;

export type AlertVariant = "standard" | "filled" | "outlined";
export type AlertSeverity = "success" | "info" | "warning" | "error" | "none";

type SeverityVariant =
  | {
      severity?: "success" | "info" | "warning" | "error";
      variant?: AlertVariant;
    }
  | { severity: "none"; variant?: "standard" };

export type AlertProps = Omit<
  MuiAlertProps,
  "severity" | "variant" | "color" | "iconMapping"
> &
  SeverityVariant & {
    title?: ReactNode;
    "data-testid"?: string;
  };

const severityIconName: Record<AlertSeverity, IconName> = {
  success: "success",
  info: "info",
  warning: "warning",
  error: "error",
  none: "help",
};

type StatusRole = "success" | "accent" | "attention" | "danger";
const statusRole: Record<Exclude<AlertSeverity, "none">, StatusRole> = {
  success: "success",
  info: "accent",
  warning: "attention",
  error: "danger",
};

/** Surface, border, text and icon colors for one color scheme. */
function buildToneSx(
  severity: AlertSeverity,
  variant: AlertVariant,
  { fgColor, bgColor, borderColor }: SchemeTokens,
): Record<string, unknown> {
  if (severity === "none") {
    return {
      backgroundColor: bgColor.neutralMuted,
      color: fgColor.default,
      border: "none",
      "& .ds-alert-icon": { color: fgColor.default },
    };
  }

  const role = statusRole[severity];

  if (variant === "filled") {
    return {
      backgroundColor: bgColor[`${role}Emphasis`],
      color: fgColor.onEmphasis,
      border: "none",
      "& .ds-alert-icon": { color: fgColor.onEmphasis },
    };
  }

  if (variant === "outlined") {
    return {
      backgroundColor: "transparent",
      color: fgColor.default,
      border: `1px solid ${borderColor[`${role}Emphasis`]}`,
      "& .ds-alert-icon": { color: fgColor[role] },
    };
  }

  return {
    backgroundColor: bgColor[`${role}Muted`],
    color: fgColor.default,
    border: "none",
    "& .ds-alert-icon": { color: fgColor[role] },
  };
}

/**
 * Padding and weight are scheme-independent; the colors are not. The light
 * values apply directly and the dark ones under `DARK_SCHEME`. A theme without
 * `cssVariables` never renders that attribute, so it keeps the light values
 * unconditionally.
 */
function buildSurfaceSx(
  severity: AlertSeverity,
  variant: AlertVariant,
): SxProps<Theme> {
  return {
    padding: stackPaddingCondensed,
    "& .MuiAlert-message": {
      fontWeight: weight.regular,
      width: "100%",
      padding: 0,
    },
    ...buildToneSx(severity, variant, tokens.theme.light),
    [DARK_SCHEME]: buildToneSx(severity, variant, tokens.theme.dark),
  };
}

// A plain Box, not MUI's AlertTitle: AlertTitle hardcodes `gutterBottom`
// (an extra 0.35em bottom margin) and reads the theme's Typography variant
// scale for weight/size, both of which fight the explicit values a locked
// title spec needs. Rendering it directly keeps spacing to the grid's own
// `rowGap` and keeps the weight the one this token actually renders at.
const titleSx: SxProps<Theme> = (theme) => ({
  margin: 0,
  fontFamily: "inherit",
  fontWeight: weight.semibold,
  fontSize: theme.typography.pxToRem(body.fontSize.medium),
  lineHeight: body.lineHeight.medium,
});

/**
 * Status alert wrapping MUI's Alert, bound to Centro semantic tokens.
 *
 * Compose a bottom-actions row through `children`: up to two `<Link>`
 * elements separated by an unstyled "·" — the separator inherits Alert's
 * text color automatically (default text color, or `onEmphasis` for
 * `variant="filled"`), so it needs no color prop of its own.
 */
export const Alert = forwardRef<HTMLDivElement, AlertProps>((props, ref) => {
  const {
    severity = "info",
    variant = "standard",
    title,
    icon,
    action,
    onClose,
    sx,
    children,
    "data-testid": dataTestId = "ds-alert",
    ...rest
  } = props;

  const iconContent =
    icon === false
      ? false
      : (icon ?? (
          <Icon name={severityIconName[severity]} size="sm" color="inherit" />
        ));

  const dismissButton = onClose ? (
    <IconButton
      size="small"
      color="inherit"
      aria-label="Close"
      title="Close"
      onClick={onClose}
      data-testid={`${dataTestId}-close`}
    >
      <Icon name="close" size="sm" color="inherit" />
    </IconButton>
  ) : null;

  const combinedAction =
    action || dismissButton ? (
      <Stack
        direction="row"
        alignItems="center"
        spacing={0.5}
        data-testid={`${dataTestId}-action`}
        sx={{ flexShrink: 0 }}
      >
        {action}
        {dismissButton}
      </Stack>
    ) : null;

  return (
    <MuiAlert
      ref={ref}
      data-testid={dataTestId}
      severity={severity === "none" ? "info" : severity}
      variant={variant}
      icon={false}
      sx={[buildSurfaceSx(severity, variant), sx] as SxProps<Theme>}
      {...rest}
    >
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: "auto 1fr auto",
          columnGap: stackPaddingCondensed,
          rowGap: stackPaddingCondensed,
          alignItems: "center",
        }}
      >
        {iconContent !== false && (
          <Box
            className="ds-alert-icon"
            data-testid={`${dataTestId}-icon`}
            sx={{ display: "flex", gridColumn: 1, gridRow: 1 }}
          >
            {iconContent}
          </Box>
        )}
        {title && (
          <Box
            data-testid={`${dataTestId}-title`}
            sx={[titleSx, { gridColumn: 2, gridRow: 1 }] as SxProps<Theme>}
          >
            {title}
          </Box>
        )}
        {combinedAction && (
          <Box sx={{ gridColumn: 3, gridRow: 1 }}>{combinedAction}</Box>
        )}
        {children ? (
          <Box sx={{ gridColumn: 2, gridRow: title ? 2 : 1 }}>{children}</Box>
        ) : (
          !title && <Box sx={{ gridColumn: 2, gridRow: 1 }} />
        )}
      </Box>
    </MuiAlert>
  );
});

Alert.displayName = "Alert";
